import styled from "@emotion/styled";
import { ttNormsFontFamily } from "../../../fonts/fonts";
import { MakeOptional } from "../../../util/MakeOptional";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
import { RequiredThemeKeys } from "../../../util/theme";
import { withFocusToggleClassName } from "../../../util/withFocusClassName";
import { avatarTheme } from "../avatarTheme";
import { SizeTypes } from "../types";

interface AvatarRootProps {
    showRing: boolean;
    size: SizeTypes;
    themeMode: RequiredThemeKeys;
}

export const AvatarRoot = withFocusToggleClassName(styled("div")<AvatarRootProps>`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    flex-grow: 0;
    height: ${getAvatarSize}px;
    width: ${getAvatarSize}px;
    color: var(--smar--lc--avatar--text-color);

    background: var(--smar--lc--avatar--background);
    :hover {
        background-color: var(--smar--lc--avatar--background--hover);
    }
    background-size: cover;

    font-size: ${getFontSize}px;
    // We do want the variable font to be used, but if it's not available, fall back to the old one.
    font-family: "TT Norms Pro var", ${ttNormsFontFamily};
    font-weight: ${({ themeMode }) => avatarTheme.weight(themeMode)};

    white-space: nowrap;

    box-sizing: border-box;
    border-radius: 50%;
    border-style: solid;
    border-color: var(--smar--lc--avatar--border-color);
    border-width: ${getBorderSize}px;

    cursor: default;

    ${(props) =>
        createFocusRingStyles({
            theme: props.themeMode,
            gap: avatarTheme.focusRingGap(props.themeMode),
            componentBorderWidth: Math.floor(getBorderSize(props)),
            componentRadius: "50%",
        })};
`);
AvatarRoot.displayName = "Container";

type SizeMap = {
    [key in SizeTypes]: number;
};

const textSizeMap: SizeMap = {
    xxSmall: 10,
    xSmall: 10,
    small: 10,
    medium: 12,
    large: 18,
    xLarge: 24,
    xxLarge: 36,
};

function getFontSize({ size }: AvatarRootProps): number {
    return textSizeMap[size];
}

const avatarSizeMap: SizeMap = {
    xxSmall: 20,
    xSmall: 24,
    small: 28,
    medium: 32,
    large: 48,
    xLarge: 64,
    xxLarge: 96,
};

function getAvatarSize({ size }: AvatarRootProps): number {
    return avatarSizeMap[size];
}

// Just using this nice li'l utility type from our boi Matt Pocock to make the types read nicer for
// our users. This deprecation is already confusing enough...
type PrettyIt<T> = {
    [key in keyof T]: T[key];
    // eslint-disable-next-line @typescript-eslint/ban-types
} & {};

/**
 *
 * @param size Given a size, returns the size in pixels of the avatar.
 */
export function avatarSizes(size: SizeTypes): number;

/**
 *
 * @deprecated please don't use this. It was accidentally exposed. I'm sorry, proofing devs :(
 */
export function avatarSizes(config: PrettyIt<MakeOptional<AvatarRootProps, "showRing" | "themeMode">>): number;

/**
 * This is used in a few places, but it's not terribly useful and it'd be nice to get rid of it.
 * Unfortunately, it's exposed on the lodestar-core API.
 *
 * It has this weird signature because we accidentally change the API and some packages started
 * using it after the change. Packages using it before the change still had the old signature. We'll
 * support both until we can remove it so we don't break anyone else.
 *
 * @deprecated please don't use this. Just pass in a size
 */
export function avatarSizes(sizeOrObj: SizeTypes | MakeOptional<AvatarRootProps, "showRing" | "themeMode">): number {
    if (typeof sizeOrObj === "string") {
        return avatarSizeMap[sizeOrObj];
    } else {
        return avatarSizeMap[sizeOrObj.size];
    }
}

function getBorderSize({ size, showRing }: AvatarRootProps): number {
    if (!showRing) {
        return 0;
    }
    switch (size) {
        case "xxSmall":
            return 1;
        case "xSmall":
            return 1.5;
        case "small":
            return 2;
        case "medium":
        default:
            return 2.5;
    }
}
